import { useState } from "react";
import { AlertTriangle, Check, Download, Pencil, Play, RefreshCw, RotateCcw, ScanFace } from "lucide-react";
import { mediaSrc } from "@/lib/api";

const STATUS_CLS = {
  queued: "bg-muted text-muted-foreground",
  processing: "bg-primary/15 text-primary",
  rendered: "bg-primary/15 text-primary",
  failed: "bg-destructive/15 text-destructive",
};

export const VariationCard = ({ v, onApprove, onEdit, onRegenerate, onDownload, onRetry }) => {
  const [playing, setPlaying] = useState(false);
  const p = v.plan;
  return (
    <div className={`card-surface card-hover overflow-hidden flex flex-col ${v.approved ? "approved-glow" : ""}`} data-testid={`variation-card-${v.index}`}>
      <div className="relative bg-black aspect-[4/5] overflow-hidden">
        {v.status === "rendered" && playing ? (
          <video src={mediaSrc(v.preview_url)} controls autoPlay playsInline className="absolute inset-0 w-full h-full object-contain" data-testid={`variation-video-${v.index}`} onEnded={() => setPlaying(false)} />
        ) : v.status === "rendered" ? (
          <button onClick={() => setPlaying(true)} className="absolute inset-0 group" data-testid={`variation-play-${v.index}`} aria-label="Preview">
            <img src={mediaSrc(v.thumb_url)} alt="" className="w-full h-full object-contain" />
            <span className="absolute inset-0 grid place-items-center bg-black/30 opacity-80 group-hover:opacity-100 transition-opacity">
              <span className="h-12 w-12 rounded-full bg-primary text-primary-foreground grid place-items-center"><Play size={18} className="ml-0.5" /></span>
            </span>
          </button>
        ) : v.status === "failed" ? (
          <div className="absolute inset-0 grid place-items-center p-4 text-center">
            <div>
              <AlertTriangle className="mx-auto text-destructive mb-2" size={22} />
              <p className="text-xs text-destructive font-mono break-words" data-testid={`variation-error-${v.index}`}>{v.error || "Render failed"}</p>
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 shimmer" />
        )}
        <span className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${STATUS_CLS[v.status]}`} data-testid={`variation-status-${v.index}`}>
          {v.status === "processing" && <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary mr-1 pulse-dot" />}{v.status === "processing" && v.worker_stage ? `${v.worker_stage} ${v.worker_progress || 0}%` : v.status}
        </span>
        <span className="absolute top-2 right-2 rounded-full bg-black/60 text-white px-2 py-0.5 text-[10px] font-mono">#{v.index + 1}</span>
        {v.near_duplicate_of && (
          <span className="absolute bottom-2 left-2 rounded-full bg-amber-500 text-black px-2 py-0.5 text-[10px] font-mono flex items-center gap-1" data-testid={`variation-duplicate-${v.index}`}><AlertTriangle size={10} /> near-dupe {v.similarity}%</span>
        )}
      </div>
      <div className="p-3 space-y-2 flex-1 flex flex-col">
        <p className="text-sm font-semibold leading-snug line-clamp-2" data-testid={`variation-hook-${v.index}`}>{v.hook || "…"}</p>
        <div className="flex flex-wrap gap-1 text-[10px] font-mono text-muted-foreground">
          <span className="rounded bg-muted px-1.5 py-0.5">{p.start}s +{p.duration}s</span>
          <span className="rounded bg-muted px-1.5 py-0.5">{p.framing}</span>
          <span className="rounded bg-muted px-1.5 py-0.5">{p.pacing}</span>
          <span className="rounded bg-muted px-1.5 py-0.5">{p.hook_style}</span>
          <span className="rounded bg-muted px-1.5 py-0.5">{p.caption_style}</span>
          {v.face_tracked && <span className="rounded bg-primary/15 text-primary px-1.5 py-0.5 flex items-center gap-1" data-testid={`variation-face-tracked-${v.index}`}><ScanFace size={10} /> face-tracked</span>}
        </div>
        <div className="mt-auto flex items-center gap-1 pt-1">
          {v.status === "rendered" && (
            <>
              <button onClick={() => onApprove(v)} className={`h-8 flex-1 rounded-full text-xs font-semibold flex items-center justify-center gap-1 transition-colors ${v.approved ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary"}`} data-testid={`variation-approve-${v.index}`}>
                <Check size={13} /> {v.approved ? "Approved" : "Approve"}
              </button>
              <button onClick={() => onEdit(v)} className="h-8 w-8 rounded-full border border-border grid place-items-center hover:border-primary transition-colors" data-testid={`variation-edit-${v.index}`} aria-label="Edit"><Pencil size={13} /></button>
              <button onClick={() => onRegenerate(v)} className="h-8 w-8 rounded-full border border-border grid place-items-center hover:border-primary transition-colors" data-testid={`variation-regenerate-${v.index}`} aria-label="Regenerate"><RefreshCw size={13} /></button>
              <button onClick={() => onDownload(v)} className="h-8 w-8 rounded-full border border-border grid place-items-center hover:border-primary transition-colors" data-testid={`variation-download-${v.index}`} aria-label="Download"><Download size={13} /></button>
            </>
          )}
          {v.status === "failed" && (
            <button onClick={() => onRetry(v)} className="h-8 flex-1 rounded-full border border-destructive/50 text-destructive text-xs font-semibold flex items-center justify-center gap-1 hover:bg-destructive/10 transition-colors" data-testid={`variation-retry-${v.index}`}>
              <RotateCcw size={13} /> Retry render
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
